import { useEnv } from '@/composables/enviromentComposable'
import { createFetch } from '@vueuse/core'
import { useToast } from 'primevue/usetoast'
import type { Ref } from 'vue'
import Keycloak, { type KeycloakInitOptions } from 'keycloak-js'

const env = useEnv()

export interface StammdatenApiOptions {
  isLoading?: Ref<boolean>
  basePath?: string
}

export const keycloak = new Keycloak({
  url: env.KEYCLOAK_AUTH_URI, // Keycloak server URL
  realm: env.KEYCLOAK_REALM, // Your realm name
  clientId: env.KEYCLOAK_CLIENT_ID // Your client ID
})

let keycloakInitialization: Promise<boolean> | null = null

export function initializeKeycloak(redirectUri: string) {
  if (env.AUTH_DISABLED) {
    return Promise.resolve(true)
  }

  if (keycloak.authenticated) {
    return Promise.resolve(true)
  }

  if (keycloakInitialization) {
    return keycloakInitialization
  }

  const initOptions: KeycloakInitOptions = {
    onLoad: 'login-required',
    redirectUri,
    checkLoginIframe: false
  }

  keycloakInitialization = keycloak.init(initOptions).catch((error) => {
    keycloakInitialization = null
    throw error
  })

  return keycloakInitialization
}

const checkTokens = async () => {
  if (env.AUTH_DISABLED) {
    return
  }

  try {
    await keycloak.updateToken(5)
  } catch (error) {
    console.error('Failed to refresh the token.', error)
  }
}

export function useStammdatenApi({ isLoading, basePath }: StammdatenApiOptions = {}) {
  const toast = useToast()
  const normalizedBasePath = basePath?.replace(/^\/+|\/+$/g, '')
  const apiUrl = env.STAMMDATEN_API_URL ? env.STAMMDATEN_API_URL : '/api'
  const baseUrl = normalizedBasePath ? `${apiUrl}/${normalizedBasePath}/` : `${apiUrl}/`

  return createFetch({
    baseUrl,
    mode: 'no-cors',
    options: {
      async beforeFetch({ options }) {
        if (isLoading) isLoading.value = true
        await checkTokens()

        if (!env.AUTH_DISABLED) {
          ;(options.headers as Record<string, string>)['Authorization'] = `Bearer ${keycloak.token}`
        }

        return { options }
      },
      afterFetch(ctx) {
        if (isLoading) isLoading.value = false
        return ctx
      },
      onFetchError(ctx) {
        toast.add({ severity: 'error', summary: 'Error', detail: String(ctx.error), life: 3000 })
        console.error(ctx.response?.url, ctx.error, ctx.response?.status, ctx.data)
        ctx.data = null
        return ctx
      }
    }
  })
}
